import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  CONDITION_AXES,
  INELIGIBLE_COPY,
  playedEvidenceLabel,
  RATING_SCALE,
  axesForSurface,
  axisAnchorText,
  draftFromOwnRating,
  headlineSeason,
  priorSeasons,
  ratingBodyFromDraft,
  ratingCountLabel,
  ratingDraftComplete,
  seasonHistoryLabel,
  turnGripLabel,
} from "../lib/facilityRatings";

/**
 * Issue #211 (ratings half) — visitor ratings of one playing surface,
 * rendered as a SIBLING of the condition control on the same facility row:
 * `condition` is what the record-keeper says it is, this is what the people
 * who played on it found, and #211's whole credibility mechanic is the two
 * sitting next to each other answering different questions.
 *
 * WHAT THIS COMPONENT CAN NEVER SHOW: who rated. The wire carries
 * aggregates, counts and the caller's own row only (Q9(a), the junior
 * consideration #169) — there is no per-rater data here to leak, and no
 * prop for any.
 *
 * THE CONTROL APPEARS WITH ELIGIBILITY, never before: `can_rate` is the
 * server's own answer to "would the PUT accept this caller", so nobody is
 * offered a button that 403s. An ineligible viewer sees the aggregates and
 * one sentence saying how rating is earned (`INELIGIBLE_COPY`).
 */

/**
 * ONE fetch per venue detail, shared by every facility row — the hook the
 * detail dialog's facility list mounts. `apply` folds a PUT response back
 * in so the aggregate on screen is the SERVER'S verdict, never a locally
 * recomputed one (the `onFacilityRated` rule, one table down).
 */
export function useGroundRatings(groundId) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    if (!groundId) return undefined;
    api
      .get(`/grounds/${groundId}/ratings`)
      .then((r) => { if (alive) setData(r.data); })
      // A ratings read failing must not degrade the venue detail around
      // it — the list renders, ratings just say nothing (same posture as
      // `loadFacilities`).
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [groundId]);

  const apply = useCallback((facilitySummary) => {
    setData((prev) => {
      if (!prev) return prev;
      const rest = prev.facilities.filter((f) => f.facility_id !== facilitySummary.facility_id);
      return { ...prev, facilities: [...rest, facilitySummary] };
    });
  }, []);

  const byFacility = useMemo(() => {
    const map = new Map();
    for (const f of data?.facilities || []) map.set(f.facility_id, f);
    return map;
  }, [data]);

  return { data, byFacility, apply };
}

/** The count-forward badge: "4 ratings · avg 3.8", never a bare average of one. */
function RatingHeadline({ entry, currentSeason, testId }) {
  const current = headlineSeason(entry, currentSeason);
  // "No ratings THIS SEASON" when history exists but this season is quiet
  // — saying "No ratings yet" over a visible history line would be false.
  const label = current
    ? ratingCountLabel(current.count, current.overall)
    : (entry?.count ? "No ratings this season" : ratingCountLabel(0));
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-indigo-50 text-indigo-800"
      data-testid={testId}
    >
      <Star size={10} />
      {label}
      {current && (entry?.count ?? 0) > current.count ? " this season" : ""}
    </span>
  );
}

/**
 * The CONDITION averages, when the server sent them — and CHARACTER on its
 * own line, never among them: Turn & grip describes what the surface IS
 * (seaming vs turning), not how good it is, so the server keeps it out of
 * `averages`/`overall` and this component keeps it visually apart too.
 */
function AxisAverages({ season, testId }) {
  const character = turnGripLabel(season?.turn_grip);
  return (
    <>
      {season?.averages && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-600" data-testid={testId}>
          {CONDITION_AXES.map(({ key, label }) => (
            <span key={key}>{label} {season.averages[key]}</span>
          ))}
        </div>
      )}
      {character && (
        <p className="mt-0.5 text-[10px] text-slate-500 italic" data-testid={`${testId}-character`}>
          {character}
        </p>
      )}
    </>
  );
}

/**
 * The axis chips themselves — one row of 1–5 targets per axis for the given
 * surface, driven by a caller-owned `draft`. Extracted from `RatingEditor`
 * (#581) so the log-a-match and training forms can capture the SAME verdict
 * inline without a second copy of the axis list: `axesForSurface` stays the
 * one source of which axes exist and Martin's anchors render beside each,
 * whether the chips sit on the grounds map or in a log form. Presentational
 * only — it never touches the API; whoever mounts it owns the save (an
 * immediate PUT on the map, a deferred one after the match row exists in the
 * forms). 44px targets, the phone-at-a-ground rule (#134).
 */
export function RatingAxisFields({ surface, draft, setDraft, saving = false, testId, tone }) {
  // #1029 — `tone="saved"` is the log flow's step 3 (`SavedRating.dc.html`,
  // D204.1): the drawn full-width chips and quiet type, as inline tokens
  // (`index.css` remaps `text-slate-*` with `!important`, so classes cannot
  // carry these). Absent, the grounds map's own markup below is unchanged.
  if (tone === "saved") {
    return (
      <>
        {axesForSurface(surface).map((axis) => {
          const { key, label, optional } = axis;
          return (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
                {label}
                {optional && (
                  <span style={{ marginLeft: 4, fontWeight: 400, letterSpacing: 0, textTransform: "none", color: "var(--nw-ink-muted)" }}>(optional)</span>
                )}
              </div>
              <div data-testid={`${testId}-${key}-anchors`} style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>
                {axisAnchorText(axis)}
              </div>
              <div role="radiogroup" aria-label={label} style={{ display: "flex", gap: 6, marginTop: 2 }}>
                {RATING_SCALE.map((n) => {
                  const chosen = draft[key] === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      disabled={saving}
                      onClick={() => setDraft((d) => ({ ...d, [key]: optional && d[key] === n ? null : n }))}
                      data-testid={`${testId}-${key}-${n}`}
                      style={{
                        flex: 1, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center",
                        borderRadius: 10, fontSize: 15, fontWeight: 600,
                        border: chosen ? "2px solid var(--nw-navy)" : "1px solid var(--nw-line)",
                        background: chosen ? "var(--nw-navy)" : "var(--nw-surface)",
                        color: chosen ? "#FFFFFF" : "var(--nw-ink)",
                        opacity: saving ? 0.6 : 1,
                        cursor: saving ? "default" : "pointer",
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </>
    );
  }
  return (
    <>
      {axesForSurface(surface).map((axis) => {
        const { key, label, optional } = axis;
        return (
        <div key={key} className="mb-1.5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            {label}
            {optional && <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">(optional)</span>}
          </div>
          {/* Martin's anchors, beside the axis they define — two raters
              are not scoring the same thing without them (#211). */}
          <div className="text-[10px] text-slate-400" data-testid={`${testId}-${key}-anchors`}>
            {axisAnchorText(axis)}
          </div>
          <div className="mt-0.5 flex gap-1.5" role="radiogroup" aria-label={label}>
            {RATING_SCALE.map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={draft[key] === n}
                disabled={saving}
                onClick={() => setDraft((d) => ({ ...d, [key]: optional && d[key] === n ? null : n }))}
                data-testid={`${testId}-${key}-${n}`}
                className={`inline-flex items-center justify-center rounded-md border w-9 min-h-11 text-[12px] font-semibold transition disabled:opacity-60 ${draft[key] === n
                  ? "border-[#0B2E59] bg-[#0B2E5908] text-[#0B2E59]"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        );
      })}
    </>
  );
}

/**
 * The inline editor — one row of 1–5 chips per axis, prefilled from the
 * caller's own rating, saved whole (the PUT refuses a partial verdict on
 * the condition trio, so Save stays disabled until those are chosen).
 * Martin's scale anchors sit under each label — "a 3 on an unanchored axis
 * means whatever the rater assumed it meant". WHICH axes depends on the
 * surface: turf gets Turn & grip (optional — tap the chip again to
 * withdraw a spin verdict); synthetic/matting/concrete get the condition
 * trio only, because there spin assistance is the material's property and
 * the API refuses the axis. 44px targets, the page's phone-at-a-ground
 * rule (#134).
 */
function RatingEditor({ groundId, facilityId, surface, myRating, onSaved, onClose, testId }) {
  const [draft, setDraft] = useState(() => draftFromOwnRating(myRating));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (saving || !ratingDraftComplete(draft)) return;
    setSaving(true);
    setError("");
    try {
      const res = await api.put(`/grounds/${groundId}/facilities/${facilityId}/rating`, ratingBodyFromDraft(draft, surface));
      // The FACILITY SUMMARY off the wire, not a locally recomputed one —
      // it already carries the caller's own rating and the refreshed
      // season groups.
      onSaved(res.data.facility);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2" data-testid={testId}>
      <RatingAxisFields surface={surface} draft={draft} setDraft={setDraft} saving={saving} testId={testId} />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !ratingDraftComplete(draft)}
          data-testid={`${testId}-save`}
          className="inline-flex items-center gap-1 rounded-md bg-[#0B2E59] px-3 min-h-11 text-[11px] font-semibold text-white disabled:opacity-50"
        >
          {saving && <Loader2 size={11} className="animate-spin" />}
          Save rating
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          data-testid={`${testId}-cancel`}
          className="rounded-md border border-slate-200 px-3 min-h-11 text-[11px] font-semibold text-slate-600"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One facility's ratings block. `entry` is the server's summary for this
 * facility (or undefined — no ratings yet), `canRate` the server's
 * eligibility verdict, `currentSeason` the server's season key.
 */
export default function FacilityRatingPanel({
  groundId,
  facilityId,
  surface,
  /** #544's kind ('field' | 'net' | null) — only for the evidence label. */
  kind = null,
  entry,
  canRate,
  /**
   * #546 — true when the caller's OWN matches/sessions name this facility
   * (`played_facility_ids`). Preference, not permission: it adds the
   * evidence line below and changes nothing about whether the rate control
   * renders (`canRate` alone decides that).
   */
  playedThisSurface = false,
  currentSeason,
  archived = false,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const testId = `facility-ratings-${facilityId}`;
  const prior = priorSeasons(entry, currentSeason);
  const mine = entry?.my_rating || null;
  const current = headlineSeason(entry, currentSeason);

  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Player ratings</span>
        <RatingHeadline entry={entry} currentSeason={currentSeason} testId={`${testId}-headline`} />
        {/* #546 — the played-here evidence, named at the surface it belongs
            to. Says something only where the caller's own record does. */}
        {playedThisSurface && (
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
            style={{ background: "#D4A01720", color: "#8A6D0A" }}
            data-testid={`${testId}-played`}
          >
            🏆 {playedEvidenceLabel(kind)}
          </span>
        )}
      </div>
      <AxisAverages season={current} testId={`${testId}-averages`} />
      {prior.length > 0 && (
        <p className="mt-0.5 text-[10px] text-slate-500" data-testid={`${testId}-history`}>
          {/* Older seasons stay visible and stay LABELLED as older — a
              resurfaced net is not judged on last year (#211). */}
          Earlier: {prior.map(seasonHistoryLabel).join(" · ")}
        </p>
      )}
      {mine && !editing && (
        <p className="mt-0.5 text-[10px] text-slate-600" data-testid={`${testId}-mine`}>
          {/* Only axes with a verdict: turn_grip is optional (and absent
              off turf), and "Turn & grip null" is not a sentence. */}
          Yours: {axesForSurface(surface)
            .filter(({ key }) => mine[key] != null)
            .map(({ key, label }) => `${label} ${mine[key]}`)
            .join(" · ")}
        </p>
      )}
      {canRate && !archived && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`${testId}-open`}
          className="mt-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-600 hover:border-slate-400"
        >
          <Star size={11} />
          {mine ? "Update your rating" : "Rate this surface"}
        </button>
      )}
      {editing && (
        <RatingEditor
          groundId={groundId}
          facilityId={facilityId}
          surface={surface}
          myRating={mine}
          onSaved={onSaved}
          onClose={() => setEditing(false)}
          testId={`${testId}-editor`}
        />
      )}
      {!canRate && (
        <p className="mt-0.5 text-[10px] text-slate-500" data-testid={`${testId}-ineligible`}>
          {INELIGIBLE_COPY}
        </p>
      )}
    </div>
  );
}
